<template>
  <div class="text-center" v-if="deviceList.length || allowDisable">
    <div :id="`select-${ device }`" class="select-media">
      <select :id="`${ device }-dropdown`" :data-input-kind="inputType" v-model="selected" v-on:change="changeInput" class="mediaDeviceSelect">
        <option v-for="(dev, idx) in deviceList" v-bind:value="dev.deviceId" :key="dev.deviceId" >
          {{ dev.label }}
        </option>
        <option v-if="allowDisable" value="disabled" id="disabled" :selected="inputId === 'disabled'" ><translate>Disabled</translate></option>
      </select>
    </div>
  </div>

</template>

<script>
  import { promiseTimeout } from '@/../../shared/src/utils.js'

  /**
  * A dropdown list to change the input device for video or audio
  */
  export default {
    name: 'MediaSelector',
    data () {
      return {
        'deviceList': [],
        'selected': null,
        'label': '',
        'name': '',
        'getPermissionFirst': true
      }
    },

    props: {
      /** String (camera | microphone): to display to user which device the options are for */
      'device': {'type': String, 'default': 'camera'},
      /** String (video | audio): indicate to the parent which device to changw when the dropdown is changed  */
      'inputType': {'type': String, 'default': 'video'},
      /** String: The id of the currently selected device  */
      'inputId': {'type': String},
      /** Boolean: Whether to allow disabling of media input altogether  */
      'allowDisable': {'type': Boolean, 'default': false},

      /** String (input | output): used to select audio output device **/
      'ioType': {'type': String, 'default': 'input'},

      'selectedDevice': {},
      'resetDevices': {'type': Number, 'default': 0}
    },

    watch: {
      resetDevices () {
        this.resetDeviceOptions()
      }
    },

    methods: {

      /**  Event handler for the 'change' event in the component dropdown.
      * Emits a custom event called `"changeDevice"` with a payload object containing the inputType and the deviceId of the selected device
      * @public
      * */
      changeInput () {

        const selectedDevice = this.deviceList.find((d) => d.deviceId === this.selected)

        this.$emit('changeDevice', {
          'inputType': this.inputType,
          'deviceId': this.selected,
          'label': selectedDevice ? selectedDevice.label : 'disabled',
          'ioType': this.ioType,
          'name': this.device
        })
        /** Emits a custom event called `"changeDevice"` with a payload object containing the inputType and the deviceId of the selected device
        Eg: `{ 'inputType': this.inputType, 'deviceId': this.selected }`
         * @event changeDevice
         * @type {custom} */
      },

      /**  Wrapper function for the native `navigator.mediaDevices.enumerateDevices()` browser api call
      * @public
      * @returns Promise
      **/
      async getDevices () {
        try {
          if (this.getPermissionFirst) {
            return navigator.mediaDevices.getUserMedia({ audio: this.inputType === 'audio', video: this.inputType === 'video' })
            .then((userMedia) => {
                return navigator.mediaDevices.enumerateDevices()
                  .then((devices) => {
                      userMedia.getTracks().map((track) => {
                        track.stop()
                        userMedia.removeTrack(track)
                      })
                      return Promise.resolve(devices)
                  })
            })
          }
          return navigator.mediaDevices.enumerateDevices()
        } catch (err) {
          if (err.message.includes('Promise Timed out')) {
            this.getPermissionFirst = false
            return this.getDevices()
          }
          return Promise.reject(err)
        }
      },

      /**  Get's a list of media devices available from the browser
      * Adds the relevant devices (audio or video) to the deviceList property for display as options in the dropdown
      * If there are no devices it emits a `noDevicesFound` event
      * @public
      **/
      selectDeviceOptions () {

        return this.getDevices()
          .then((deviceList) => {
            this.deviceList = []
            deviceList.forEach((device) => {
              if (device.kind === `${this.inputType}${this.ioType}`) {
                this.deviceList.push(device)
                if (!this.selected) {
                  this.selected = device.deviceId
                  this.changeInput()
                }
              }
            })
            if (!this.deviceList.length) {
            /** Emits a custom event called `noDevicesFound` with a string indicating the device type (video | audio)
            * @event noDevicesFound
            * @type {custom} */
              this.$emit('noDevicesFound', this.inputType)
            }
          })
          .catch((err) => {
            this.logError({
              'type': 'errors/navigatorMediaDevices',
              'payload': {
                'inputType': this.inputType,
                'ioType': this.ioType,
                'error': {'name': err.name, 'message': err.message}
              }
            })
          })
      },

      resetDeviceOptions () {
        this.selected = null
        this.selectDeviceOptions()
      }
    },

    mounted () {
      this.selected = this.inputId
      this.selectDeviceOptions()
    }

  }
</script>

<style scoped>
  h5{
    text-transform: capitalize;
  }

  .mediaDeviceSelect{
    display: inline!important;
    max-width: 100%;
  }
</style>
